import { Api } from '../../Api/Api';
import { NetError } from '../../Api/NetError';
import { IPdfWarning, PDF_WARNING_MISSING_PRICING } from '../../Api/pdfWarnings';
import { AppDispatch, RootState } from '../Store';
import { IPlanResponse, } from '../../Models/Responses/PlanResponse';
import { IGetPlansRequest, IPlanUpdateRequest, IPlanDownloadRequest } from '../../Models/Requests/PlanRequest';
import { getCurrentActingUser } from '../User/AuthSlice';
import { abortableSleep } from '../../Utility/Utils';
import { createTracedAsyncThunk } from '../../../tracing/trace';
import { getAbortReasonFallback } from '../../Utility/registerCancelableThunks';
import { anySignal } from '../../Utility/anySignal';

export interface IPlans
{
	Plans: IPlanResponse[];
	RequestedGrowerId: string;
}

export interface ILaunchPlan
{
	LaunchUrl: string;
	RequestedGrowerId: string;
}

export interface ILaunchExistingPlan extends ILaunchPlan
{
	PlanId: string;
}

export interface IKnownGetPlansError
{
	error: string;
	growerId: string;
}

export const getPlans = createTracedAsyncThunk<IPlans, IGetPlansRequest, { dispatch: AppDispatch, state: RootState, rejectValue: IKnownGetPlansError }>(
	'plans/get',
	async (context, request: IGetPlansRequest, thunkAPI) => 
	{
		const { auth, ui, } = thunkAPI.getState();
		const RequestedGrowerId = request.growerId || ui.SelectedGrowerId;
		try
		{
			const api = new Api('/api/6', auth.userAuthToken, context);
			const response = await api.getAsync<IPlanResponse[]>(
				`growers/${RequestedGrowerId}/years/${request.cropYear}/plans`,
			);

			if (response.ErrorCode === null && response.Success)
			{
				const plansResponse: IPlans = {
					Plans: response.Data,
					RequestedGrowerId
				};
				return plansResponse;
			}
			else 
			{
				return thunkAPI.rejectWithValue({ error: response.ErrorMessage, growerId: RequestedGrowerId });
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue({ error: e.message, growerId: RequestedGrowerId });
		}
	}
);

export const updatePlanStatus = createTracedAsyncThunk<IPlans, IPlanUpdateRequest, { dispatch: AppDispatch, state: RootState }>(
	'plans/post',
	async (context, request: IPlanUpdateRequest, thunkAPI) => 
	{
		try
		{
			const { auth, ui, crops } = thunkAPI.getState() as RootState;
			const RequestedGrowerId = ui.SelectedGrowerId;

			const api = new Api('/api/6', auth.userAuthToken, context);
			const response = await api.postAsync<IPlanResponse[]>(
				`plans/${request.planId}/status`,
				request.status
			);

			if (response.ErrorCode === null && response.Success)
			{
				// Use the getPlans response to return the plan list so that it contains pricing (if the user is GHX)
				const cropYear = ui.SelectedYear ? ui.SelectedYear : crops.CropYear;

				await thunkAPI.dispatch(getPlans({ cropYear: cropYear, growerId: RequestedGrowerId }));

				const plansResponse: IPlans = {
					Plans: response.Data,
					RequestedGrowerId
				};
				return plansResponse;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

type DownloadPlanPdfArgs = IPlanDownloadRequest & { __externalSignal?: AbortSignal };

const saveOrOpenPdf = (name: string, data: Blob, fallbackName: string): void =>
{
	if (window.navigator.userAgent.toLowerCase().indexOf('crios') > -1)
	{//iOS Chrome
		const typedBlob = new Blob([data], {type: 'application/pdf'});
		const link = (window.URL || window['webkitURL']).createObjectURL(typedBlob);
		window.open(link);
		return;
	}

	// Store this file as a hidden 'a' element and then automatically click it.
	const link = document.createElement('a');
	link.href = (window.URL || window['webkitURL']).createObjectURL(data);

	// The renderer always adds ".pdf" to the filename. If there's more than that, use it. Otherwise fall back on the session name.
	link.download = name?.slice(0, name.indexOf('.pdf')) ? name : fallbackName;

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export interface IDownloadPlanPdfResult
{
	// Set when the PDF downloaded but the API reported a problem the user must see (e.g. MissingPricing).
	warningMessage?: string;
}

const MISSING_PRICING_HEADLINE = 'Pricing is missing for some seeds on this proposal:';
const MISSING_PRICING_FALLBACK_HEADLINE = 'Pricing is missing for some seeds on this proposal.';
const MISSING_PRICING_EXPLANATION = 'Your PDF was downloaded, but these seeds show --- instead of a price.';

// The API sends "Pricing could not be found for: A (X); B (Y)" - list each seed on its own line.
const formatMissingPricingMessage = (apiMessage: string): string =>
{
	const separatorIndex = apiMessage.indexOf(':');
	const seeds = (separatorIndex >= 0 ? apiMessage.slice(separatorIndex + 1) : '')
		.split(';')
		.map(seed => seed.trim())
		.filter(seed => seed);

	if (seeds.length)
	{
		return `${MISSING_PRICING_HEADLINE}\n\n${seeds.join('\n')}\n\n${MISSING_PRICING_EXPLANATION}`;
	}

	return apiMessage
		? `${apiMessage}\n\n${MISSING_PRICING_EXPLANATION}`
		: `${MISSING_PRICING_FALLBACK_HEADLINE}\n\n${MISSING_PRICING_EXPLANATION}`;
};

// Builds the message for the global error dialog; the download itself still succeeds.
const resolvePdfWarningMessage = (warnings: IPdfWarning[]): string | undefined =>
{
	warnings
		.filter(w => w.code !== PDF_WARNING_MISSING_PRICING)
		.forEach(w => console.warn(`Unknown PDF warning code '${w.code}': ${w.message}`));

	const missingPricing = warnings.find(w => w.code === PDF_WARNING_MISSING_PRICING);
	if (!missingPricing)
	{
		return undefined;
	}

	return formatMissingPricingMessage(missingPricing.message);
};

const resolveDownloadErrorMessage = (e: unknown, combined: AbortSignal, requestId: string): string =>
{
	if (combined.aborted)
	{
		const r = (combined as any).reason ?? getAbortReasonFallback(requestId) ?? new DOMException('Aborted', 'AbortError');
		return typeof r === 'object' && r?.message ? r.message : String(r);
	}

	if (e instanceof NetError && e.errorCode === 'MissingPricingException' && e.apiMessage)
	{
		return e.apiMessage;
	}

	if (e instanceof NetError)
	{
		return e.message;
	}

	return e instanceof Error ? e.message : String(e);
};

export const downloadPlanPdf = createTracedAsyncThunk<IDownloadPlanPdfResult, DownloadPlanPdfArgs, { dispatch: AppDispatch, state: RootState }>(
	'plans/download',
	async (context, request: DownloadPlanPdfArgs, thunkAPI) =>
	{
		const { getState, rejectWithValue, signal: rtkSignal, requestId } = thunkAPI;

		// ⟵ build the merged signal the thunk will use everywhere
		const combined = anySignal([rtkSignal, request.__externalSignal]);

		try
		{
			const { auth } = getState();
			const api = new Api('/api/6', auth.userAuthToken, context);

			const PlanId = request.PlanId;
			const currentActingUser = getCurrentActingUser(getState());
			const pdfSession = await api.putAsync<string>(`plans/${PlanId}/pdf`, request.CoverConfig ?? {}, { signal: combined });

			let pdfData = undefined;
			do 
			{
				// wait half a second for each look
				await abortableSleep(500, combined);
				pdfData = await api.getFileOrErrorAsync(`pdfs/proposal/${pdfSession.Data}`, { signal: combined });
			} while(!pdfData);

			const { name, data, warnings } = pdfData;

			if (!data.size)
			{
				return thunkAPI.rejectWithValue('There was an error downloading the PDF data.');
			}

			saveOrOpenPdf(name, data, `${currentActingUser.UserName}'s Plan`);

			// Skip the dialog if the user cancelled while the final response was arriving
			return { warningMessage: combined.aborted ? undefined : resolvePdfWarningMessage(warnings) };
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return rejectWithValue(resolveDownloadErrorMessage(e, combined, requestId));
		}
	}
);

interface IDeletePlanRequest
{
	planId: string;
}
export interface IDeleteResponse
{
	planId: string;
	growerId: string;
}

export const deletePlan = createTracedAsyncThunk<IDeleteResponse, IDeletePlanRequest, { dispatch: AppDispatch, state: RootState }>(
	'plans/delete',
	async (context, request: IDeletePlanRequest, thunkAPI) =>
	{
		try
		{
			const { auth, ui } = thunkAPI.getState();
			const { SelectedGrowerId } = ui;
			const api = new Api('/api/6', auth.userAuthToken, context);

			const { planId } = request;
			const response = await api.deleteAsync(`plans/${planId}`);

			if (response.ErrorCode === null && response.Success)
			{
				return { planId, growerId: SelectedGrowerId };
			}
			else
			{
				thunkAPI.rejectWithValue('');
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const downloadMaxscriptPdf = createTracedAsyncThunk<Blob, { __externalSignal?: AbortSignal }, { dispatch: AppDispatch, state: RootState }>(
	'plans/maxscript/download',
	async (context, request: { __externalSignal?: AbortSignal }, thunkAPI) =>
	{
		const { getState, rejectWithValue, signal: rtkSignal, requestId } = thunkAPI;

		// ⟵ build the merged signal the thunk will use everywhere
		const combined = anySignal([rtkSignal, request.__externalSignal]);

		try
		{
			const { auth, ui, crops } = getState();
			const api = new Api('/api/6', auth.userAuthToken, context);

			const selectedYear = ui.SelectedYear ? ui.SelectedYear : crops.CropYear;

			const currentActingUser = getCurrentActingUser(getState());
			const pdfSession = await api.getAsync<string>(
				`growers/${ui.SelectedGrowerId}/year/${selectedYear}/maxscript/pdf`, { signal: combined });

			let pdfData = undefined;
			do 
			{
				// wait half a second for each look
				await abortableSleep(500, combined);
				pdfData = await api.getFileAsync(`pdfs/maxscript/${pdfSession.Data}`, { signal: combined });
			} while(!pdfData);

			const { name, data } = pdfData;

			if (!data.size)
			{
				return thunkAPI.rejectWithValue('There was an error downloading the PDF data.');
			}

			if (window.navigator.userAgent.toLowerCase().indexOf('crios') > -1)
			{//iOS Chrome
				const typedBlob = new Blob([data], {type: 'application/pdf'});
				const link = (window.URL || window['webkitURL']).createObjectURL(typedBlob);
				window.open(link);
			}
			else
			{
				// Store this file as a hidden 'a' element and then automatically click it.
				const link = document.createElement('a');
				link.href = (window.URL || window['webkitURL']).createObjectURL(data);

				// The renderer always adds ".pdf" to the filename. If there's more than that, use it. Otherwise fall back on the session name.
				link.download = name && name.slice(0, name.indexOf('.pdf'))
					? name : `${currentActingUser.UserName}'s Plan`;

				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			if (combined.aborted)
			{
				const r = (combined as any).reason ?? getAbortReasonFallback(requestId) ?? new DOMException('Aborted', 'AbortError');
				const msg = typeof r === 'object' && r?.message ? r.message : String(r);
				return rejectWithValue(msg);
			}
			return rejectWithValue(e.message);
		}
	}
);